import type { UnitAsset } from '../api/catalog';
import { ca } from '../i18n/ca';
import { AssetPicture } from './AssetPicture';

/** How many portraits the strip shows before it says how many more there are. */
const SHOWN = 8;

/**
 * How a faction's men look: a strip of the game's own pictures of them.
 *
 * <p>A faction's name often says nothing — "[JMS] Royal Italian Colonial
 * Corps" is a uniform before it is a name — and the flag beside it is our own
 * drawing for most mod factions. The enemy step has shown the men since
 * 2026-09-09; our own step had none, although it loads the same assets for its
 * unit column (QUICK_START_REVIEW.md F8). Only the men the game has a picture
 * of: a mod that publishes none would otherwise draw a row of empty frames
 * with names under them, which says less than the name alone.
 */
export function FactionLooks({
  title,
  hint,
  units,
  modSetHash,
}: {
  title: string;
  hint: string;
  /** The faction's men; null while nobody has answered yet. */
  units: UnitAsset[] | null;
  modSetHash?: string | null;
}) {
  const pictured = (units ?? []).filter((u) => u.picture);
  const shown = pictured.slice(0, SHOWN);
  const more = pictured.length - shown.length;
  return (
    <section className="faction-looks" aria-label={title}>
      <h3>{title}</h3>
      {units !== null && pictured.length === 0 && (
        <p className="muted small">
          {units.length > 0
            ? ca.missionUi.details.enemyLooksNoPictures
            : ca.missionUi.details.enemyLooksNone}
        </p>
      )}
      {shown.length > 0 && (
        <>
          <ul className="looks">
            {shown.map((u) => (
              <li key={u.className}>
                <AssetPicture
                  thumb
                  size={72}
                  assetClass={u.className}
                  picture={u.picture}
                  modSetHash={modSetHash}
                  name={u.displayName}
                />
                <span>{u.displayName}</span>
              </li>
            ))}
            {more > 0 && (
              <li className="more">
                <span className="chip" title={ca.missionUi.looks.moreTitle(more)}>
                  {ca.missionUi.looks.more(more)}
                </span>
              </li>
            )}
          </ul>
          <p className="muted small">{hint}</p>
        </>
      )}
    </section>
  );
}
